import { Box, Ellipsis, Stack, Text, desktopFirstMediaQuery } from '@inflearn/ds-react';

import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';

import { CourseType } from '../../../../../../resources/@types/courseSchema';
import { eventNames } from '../../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../../utils/GTM/useTrackMixpanelEvent';
import { useCourseReviewContext } from '../CourseCardContainer';
import useUrlStateForMyCourses from '../hooks/useUrlStateForMyCourses';
import { ChallengeCourseCardMetaInfo } from './ChallengeCourseCardMetaInfo';
import { CourseThumbnail } from './CourseThumbnail';
import { OfflineCourseCardMetaInfo } from './OfflineCourseCardMetaInfo';
import { OnlineCourseCardMetaInfo } from './OnlineCourseCardMetaInfo';
import type { CourseCardData } from './courseCardDataTransformer';

const CourseCard = ({
  voucherId,
  course,
  progress,
  completeCount,
  review,
  expireInformation,
  dashboardLink,
  lectureLink,
  hasCertificate,
  isArchived,
  isCancelable,
  isReviewable,
  isArchivable,
  courseType,
  ...voucher
}: CourseCardData & { courseType: CourseType }) => {
  const { t } = useTranslation();
  const { getCurrentFilterForGTM, getSortLabelForGTM } = useUrlStateForMyCourses();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const { openCertificateModal } = useCourseReviewContext();
  const { title, thumbnailUrl, isOfflineCourse, isChallengeCourse } = course;

  const handleClickCourseCardEvent = (clickSpot: 'title' | 'thumbnail') => {
    const filters = Object.values(getCurrentFilterForGTM()).filter(Boolean);
    const sort = getSortLabelForGTM();

    trackMixpanelEvent(eventNames.CLICK_MY_COURSES_CARD, {
      course_identification: course.id,
      course_title: course.title,
      course_type:
        courseType === CourseType.ON ? 'on' : courseType === CourseType.OFF ? 'off' : 'challenge',
      seq0_instructor_id: course.instructorId,
      seq0_instructor_name: course.instructorName,
      course_limit_at: voucher.limitedAt || t('무제한'),
      progress,
      click_spot: clickSpot,
      filter_my_courses: [...filters, sort],
    });
  };

  const onClickCertificate = () => {
    openCertificateModal(voucherId);
    trackMixpanelEvent(eventNames.VIEW_CERTIFICATE, {
      click_spot: 'mycourses',
    });
  };

  const isChallengeCourseEnded =
    isChallengeCourse && dayjs().isAfter(dayjs(course.challengeCourse?.endedAtUTC));

  const videoAvailable = !expireInformation.isExpired;

  return (
    <Box>
      <Stack spacing={4}>
        <CourseThumbnail
          title={title}
          link={isOfflineCourse ? dashboardLink : lectureLink}
          image={thumbnailUrl}
          alt={t('{{title}} 썸네일', { title: title })}
          videoAvailable={videoAvailable}
          hasCertificate={hasCertificate}
          isOfflineCourse={isOfflineCourse}
          onLinkClick={() => handleClickCourseCardEvent('thumbnail')}
          onClickCertificate={() => onClickCertificate()}
          disabled={!videoAvailable || isChallengeCourseEnded}
        />

        <a
          onClick={(e) => {
            e.preventDefault();
            handleClickCourseCardEvent('title');
            location.href = dashboardLink;
          }}
        >
          <Title title={title} />
        </a>
        {isOfflineCourse && (
          <OfflineCourseCardMetaInfo
            hasCertificate={hasCertificate}
            isArchived={isArchived}
            isArchivable={isArchivable}
            isCancelable={isCancelable}
            course={course}
            voucherId={voucherId}
            isReviewable={isReviewable}
            review={review}
          />
        )}
        {isChallengeCourse && (
          <ChallengeCourseCardMetaInfo
            progress={progress}
            completeCount={completeCount}
            hasCertificate={hasCertificate}
            isArchived={isArchived}
            isArchivable={isArchivable}
            isCancelable={isCancelable}
            course={course}
            voucherId={voucherId}
            isReviewable={isReviewable}
            review={review}
          />
        )}
        {!isChallengeCourse && !isOfflineCourse && (
          <OnlineCourseCardMetaInfo
            progress={progress}
            completeCount={completeCount}
            expireInformation={expireInformation}
            hasCertificate={hasCertificate}
            isArchived={isArchived}
            isArchivable={isArchivable}
            isCancelable={isCancelable}
            course={course}
            voucherId={voucherId}
            isReviewable={isReviewable}
            review={review}
          />
        )}
      </Stack>
    </Box>
  );
};

function Title({ title }: { title: string }) {
  return (
    <Box css={{ maxWidth: '100%' }}>
      <Ellipsis lineClamp={2}>
        <Text
          size="md"
          weight={500}
          css={{
            '&:hover': {
              cursor: 'pointer',
            },
            minHeight: 48,
            [desktopFirstMediaQuery.mobile]: {
              fontSize: 14,
              minHeight: 42,
            },
          }}
        >
          {title}
        </Text>
      </Ellipsis>
    </Box>
  );
}

export default CourseCard;
